import { buildHeaders, getApiBaseUrl } from '@/utils/getApiBaseUrl';

// Types for API integration (simplified versions)
interface PageVisit {
  pageId: string;
  pageUrl: string;
  pageTitle: string;
  visitTime: number;
  pageVisitNumber: string;
  exitTime?: number;
  timeSpent?: number;
  timeSpentObject?: { m: number; s: number };
  userAgent: string;
  viewport: { width: number; height: number };
  scrollDepth: number;
  interactions: number;
  isActive: boolean;
  referrer?: string;
  entryMethod?: string;
  exitMethod?: string;
  user_id?: string;
  subscriber_id?: number;
  sessionId?: string;
  deviceId?: string;
  accessToken?: string;
  cookieToken?: string;
  domain?: string;
  siteType?: string;
  deviceInfo?: any;
  userActions?: any;
  performanceMetrics?: any;
  previousAccumulatedTime?: number;
}

interface UserInteraction {
  id: string;
  type: string;
  element: string;
  elementType?: string;
  elementId?: string;
  elementClass?: string;
  elementText?: string;
  elementHref?: string;
  timestamp: number;
  pageUrl: string;
  pageVisitNumber: string;
  pageTitle: string;
  coordinates?: { x: number; y: number };
  metadata?: any;
  sessionId?: string;
  user_id?: string;
  subscriber_id?: number;
  deviceId?: string;
  accessToken?: string;
  cookieToken?: string;
  token?: string;
  domain?: string;
  siteType?: string;
  deviceInfo?: any;
}

interface TrackingData {
  pageVisits: PageVisit[];
  userActivity?: any;
  pageAnalytics?: any;
  sessionSummary?: any;
  timestamp: number;
}

export class UserTrackingService {
  private static instance: UserTrackingService;
  private batchSize = 10; // Send data in batches
  private pendingData: any[] = [];
  private syncInterval: NodeJS.Timeout | null = null;

  private constructor() {
    this.startPeriodicSync();
  }

  public static getInstance(): UserTrackingService {
    if (!UserTrackingService.instance) {
      UserTrackingService.instance = new UserTrackingService();
    }
    return UserTrackingService.instance;
  }

  public async storePageVisit(pageVisit: PageVisit, host?: string, token?: string): Promise<void> {
    try {
      const payload = {
        pageId: pageVisit.pageId,
        pageUrl: pageVisit.pageUrl,
        pageVisitNumber: pageVisit.pageVisitNumber,
        pageTitle: pageVisit.pageTitle,
        visitTime: pageVisit.visitTime,
        exitTime: pageVisit.exitTime,
        timeSpent: pageVisit.timeSpent,
        timeSpentObject: pageVisit.timeSpentObject,
        userAgent: pageVisit.userAgent,
        viewport: pageVisit.viewport,
        scrollDepth: pageVisit.scrollDepth,
        interactions: pageVisit.interactions,
        isActive: pageVisit.isActive,
        referrer: pageVisit.referrer,
        entryMethod: pageVisit.entryMethod,
        exitMethod: pageVisit.exitMethod,
        user_id: pageVisit.user_id,
        subscriber_id: pageVisit.subscriber_id,
        sessionId: pageVisit.sessionId,
        deviceId: pageVisit.deviceId,
        accessToken: pageVisit.accessToken,
        cookieToken: pageVisit.cookieToken,
        domain: pageVisit.domain,
        siteType: pageVisit.siteType,
        deviceInfo: pageVisit.deviceInfo,
        userActions: pageVisit.userActions,
        performanceMetrics: pageVisit.performanceMetrics,
        previousAccumulatedTime: pageVisit.previousAccumulatedTime
      };

      // Add to pending batch
      this.pendingData.push({
        type: 'page_visit',
        data: payload,
        timestamp: Date.now()
      });

      // console.log('📊 Page visit queued for API sync:', pageVisit.pageUrl);
      
      // Send immediately if batch is full
      if (this.pendingData.length >= this.batchSize) {
        await this.flushPendingData(host, token);
      }
    } catch (error) {
      console.error('❌ Error storing page visit:', error);
    }
  }

  public async storeUserInteraction(interaction: UserInteraction, host?: string, token?: string): Promise<void> {
    try {
      const payload = {
        id: interaction.id,
        type: interaction.type,
        element: interaction.element,
        elementType: interaction.elementType,
        elementId: interaction.elementId,
        elementClass: interaction.elementClass,
        elementText: interaction.elementText,
        elementHref: interaction.elementHref,
        timestamp: interaction.timestamp,
        pageUrl: interaction.pageUrl,
        pageVisitNumber: interaction.pageVisitNumber,
        pageTitle: interaction.pageTitle,
        coordinates: interaction.coordinates,
        metadata: interaction.metadata,
        sessionId: interaction.sessionId,
        user_id: interaction.user_id,
        subscriber_id: interaction.subscriber_id,
        deviceId: interaction.deviceId,
        accessToken: interaction.accessToken,
        cookieToken: interaction.cookieToken,
        token: interaction.token,
        domain: interaction.domain,
        siteType: interaction.siteType,
        deviceInfo: interaction.deviceInfo
      };

      // Add to pending batch
      this.pendingData.push({
        type: 'user_interaction',
        data: payload,
        timestamp: Date.now()
      });

      // console.log('📊 User interaction queued for API sync:', interaction.type);
      
      // Send immediately if batch is full
      if (this.pendingData.length >= this.batchSize) {
        await this.flushPendingData(host, token);
      }
    } catch (error) {
      console.error('❌ Error storing user interaction:', error);
    }
  }

  public async storeTrackingData(trackingData: TrackingData, host?: string, token?: string): Promise<void> {
    try {
      const payload = {
        pageVisits: trackingData.pageVisits,
        userActivity: trackingData.userActivity,
        pageAnalytics: trackingData.pageAnalytics,
        sessionSummary: trackingData.sessionSummary,
        timestamp: trackingData.timestamp
      };

      // Send complete tracking data
      // manav - commented
      // await this.sendToAPI('/api/user-tracking/complete-session', payload, host, token);
      
      // console.log('📊 Complete tracking data sent to API');
    } catch (error) {
      console.error('❌ Error storing complete tracking data:', error);
    }
  }

  private async sendToAPI(endpoint: string, data: any, host?: string, token?: string): Promise<void> {
    try {
      const baseUrl = getApiBaseUrl(host) || process.env.NEXT_PUBLIC_API_BASE_URL;
      const fullUrl = `${baseUrl}${endpoint}`;
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...buildHeaders(token || '', 'userTracking', host),
        },
        body: JSON.stringify(data),
        cache:'no-store',
          next: { revalidate: 0 }
      });

      if (response.ok) {
        // console.log('✅ Data sent successfully to:', endpoint);
      } else {
        console.warn('⚠️ Unexpected response from:', endpoint, response.status);
      }
    } catch (error: any) {
      if (error.response) {
        console.error('❌ API Error:', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('❌ Network Error:', error.message);
      } else {
        console.error('❌ Error:', error.message);
      }
      throw error;
    }
  }

  private async flushPendingData(host?: string, token?: string): Promise<void> {
    if (this.pendingData.length === 0) return;

    try {
      const batchData = [...this.pendingData];
      this.pendingData = [];

      // manav - commented
      // await this.sendToAPI('/api/user-tracking/batch', {
      //   data: batchData,
      //   batchSize: batchData.length,
      //   timestamp: Date.now()
      // }, host, token);

      // console.log(`📊 Flushed ${batchData.length} items to API`);
    } catch (error) {
      console.error('❌ Error flushing pending data:', error);
      // Re-add failed data to pending queue
      this.pendingData.unshift(...this.pendingData);
    }
  }

  private startPeriodicSync(): void {
    // Sync every 30 seconds
    this.syncInterval = setInterval(() => {
      this.flushPendingData();
    }, 20000);
  }

  public stopPeriodicSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  public async forceFlush(host?: string, token?: string): Promise<void> {
    await this.flushPendingData(host, token);
  }

  public getPendingCount(): number {
    return this.pendingData.length;
  }

  public async syncUserId(subscriber_id: number, user_id: string, device_id: string, host?: string, token?: string): Promise<void> {
    try {
      const payload = {
        subscriber_id: subscriber_id,
        user_id: user_id,
        device_id: device_id,
        domain: host || window.location.hostname,
        timestamp: Date.now(),
        syncType: 'user_id_sync'
      };

      // Add to pending batch
      this.pendingData.push({
        type: 'user_id_sync',
        data: payload,
        timestamp: Date.now()
      });

      // console.log('🌐 User ID sync queued for API:', { subscriber_id, user_id, device_id });
      
      // Send immediately for user ID sync (high priority)
      await this.flushPendingData(host, token);
    } catch (error) {
      console.error('❌ Error syncing user ID:', error);
    }
  }

  public async getUserId(subscriber_id: number, device_id: string, host?: string, token?: string): Promise<string | null> {
    try {
      const payload = {
        subscriber_id: subscriber_id,
        device_id: device_id,
        domain: host || window.location.hostname,
        timestamp: Date.now(),
        syncType: 'get_user_id'
      };

      // Add to pending batch
      this.pendingData.push({
        type: 'get_user_id',
        data: payload,
        timestamp: Date.now()
      });

      // console.log('🌐 Get user ID request queued for API:', { subscriber_id, device_id });
      
      // Send immediately for user ID request (high priority)
      await this.flushPendingData(host, token);
      
      // Note: The actual user_id response would need to be handled by the server
      // and returned through a different mechanism (like a callback or polling)
      return null;
    } catch (error) {
      console.error('❌ Error getting user ID:', error);
      return null;
    }
  }

 async validateSession(sessionId: string, deviceId: string, host?: string, token?: string): Promise<void> {
    try {
      const payload = {
        sessionId: sessionId,
        deviceId: deviceId,
        timestamp: Date.now(),
        syncType: 'session_validation'
      };

      // Add to pending batch
      // manav - commented
      // await this.sendToAPI('/api/user-tracking/validate-session', {
      //   data: payload,
      //   timestamp: Date.now()
      // }, host, token);
      // console.log('🔐 Session validation queued for API:', { sessionId, deviceId });
      
      // Send immediately for session validation (high priority)
      await this.flushPendingData(host, token);
    } catch (error) {
      console.error('❌ Error validating session:', error);
    }
  }
}

export const userTrackingService = UserTrackingService.getInstance();
